import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { isNil, omitBy } from 'lodash-es';
import { Button } from '@ds';
import { OrganType } from '@/api/apis/case';
import { EmployeeRole } from '@/api/apis/resources';
import { formatDate } from '@/utils/helpers';

type OpenAssignmentCalendarButtonProps = {
  day?: Date | number | string;
  employeeRole?: EmployeeRole;
  hubId?: string;
  organType?: OrganType;
  isContractor?: boolean;
};
const rolesWithScheduler = new Set<EmployeeRole>([
  EmployeeRole.ClinicalSpecialist,
  EmployeeRole.RetrievalSurgeon,
  EmployeeRole.ClinicalCoordinator,
  EmployeeRole.ClinicalLead,
  EmployeeRole.OrganManagementSpecialist,
]);

const getPathnameFromRole = (role?: EmployeeRole, organType?: OrganType): string => {
  switch (role) {
    case EmployeeRole.ClinicalSpecialist:
    case EmployeeRole.ClinicalLead:
      return '/schedule/cs';
    case EmployeeRole.RetrievalSurgeon:
      return organType === OrganType.Liver ? '/schedule/s-li' : '/schedule/s-htlu';
    case EmployeeRole.ClinicalCoordinator:
      return '/schedule/cc';
    case EmployeeRole.OrganManagementSpecialist:
      return '/schedule/oms';
    default:
      return '';
  }
};

export const OpenAssignmentCalendarButton = ({
  employeeRole,
  day,
  hubId,
  organType,
  isContractor,
}: OpenAssignmentCalendarButtonProps) => {
  const showButton = rolesWithScheduler.has(employeeRole as EmployeeRole) && !isContractor;

  const linkPath = useMemo(() => {
    const useToday = [
      EmployeeRole.ClinicalLead,
      EmployeeRole.ClinicalCoordinator,
      EmployeeRole.OrganManagementSpecialist,
    ].includes(employeeRole!);
    const dayParam = useToday ? new Date() : day;

    const params = {
      view: 'reservations',
      day: dayParam ? formatDate(dayParam, 'MM-dd-yyyy') : undefined,
      hubId: employeeRole === EmployeeRole.ClinicalSpecialist ? hubId : undefined,
      organ: employeeRole === EmployeeRole.RetrievalSurgeon ? organType : undefined,
    };
    const search = new URLSearchParams(omitBy(params, isNil) as Record<string, string>).toString();
    const pathname = getPathnameFromRole(employeeRole, organType);

    return { pathname, search };
  }, [day, employeeRole, hubId, organType]);

  if (!showButton) return null;

  return (
    <Button size="sm" variant="outline" disableRipple as={Link} to={linkPath} target="_blank">
      Open Calendar
    </Button>
  );
};
